import { useState, useMemo, useRef, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api, EPGProgram, WebPlayerChannel } from '@/lib/api'
import { Search, X } from 'lucide-react'

interface EPGGuideProps {
  channels: WebPlayerChannel[]
  onChannelSelect: (channel: WebPlayerChannel) => void
  isChannelPlaying: (channelId: string) => boolean
}

const CHANNEL_WIDTH = 200  // px - left column
const PIXELS_PER_MIN = 4   // 240px per hour
const ROW_HEIGHT = 60       // px per channel row
const HEADER_HEIGHT = 40    // px time header

export default function EPGGuide({ channels, onChannelSelect, isChannelPlaying }: EPGGuideProps) {
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const containerRef = useRef<HTMLDivElement>(null)
  const channelColRef = useRef<HTMLDivElement>(null)
  const timeHeaderRef = useRef<HTMLDivElement>(null)

  // Time window: -1h to +5h from current hour
  const now = new Date()
  const timeStart = new Date(now)
  timeStart.setMinutes(0, 0, 0)
  timeStart.setHours(timeStart.getHours() - 1)
  const timeEnd = new Date(timeStart)
  timeEnd.setHours(timeEnd.getHours() + 6)
  const totalMinutes = (timeEnd.getTime() - timeStart.getTime()) / 60000
  const gridWidth = totalMinutes * PIXELS_PER_MIN

  // Fetch EPG data
  const { data: epgData } = useQuery({
    queryKey: ['epgGuide', timeStart.toISOString(), timeEnd.toISOString()],
    queryFn: () => api.getEPGGuide(timeStart.toISOString(), timeEnd.toISOString()),
    refetchInterval: 5 * 60 * 1000,
    staleTime: 2 * 60 * 1000,
  })

  // EPG lookup
  const epgByChannel = useMemo(() => {
    const map = new Map<string, EPGProgram[]>()
    if (epgData?.channels) {
      for (const ch of epgData.channels) {
        map.set(ch.channel_id, ch.programs || [])
      }
    }
    return map
  }, [epgData])

  // Categories
  const categories = useMemo(() => {
    const cats = new Set<string>()
    channels.forEach(ch => { if (ch.category) cats.add(ch.category) })
    return ['all', ...Array.from(cats).sort()]
  }, [channels])

  // Filtered channels
  const filteredChannels = useMemo(() => {
    return channels.filter(ch => {
      if (selectedCategory !== 'all' && ch.category !== selectedCategory) return false
      if (searchQuery && !ch.name.toLowerCase().includes(searchQuery.toLowerCase())) return false
      return true
    }).sort((a, b) => (a.number || 999) - (b.number || 999))
  }, [channels, selectedCategory, searchQuery])

  // Time slots (30-min)
  const timeSlots = useMemo(() => {
    const slots: Date[] = []
    const t = new Date(timeStart)
    while (t < timeEnd) {
      slots.push(new Date(t))
      t.setMinutes(t.getMinutes() + 30)
    }
    return slots
  }, [])

  // Scroll to current time on mount
  useEffect(() => {
    if (containerRef.current) {
      const minutesSinceStart = (now.getTime() - timeStart.getTime()) / 60000
      containerRef.current.scrollLeft = Math.max(0, minutesSinceStart * PIXELS_PER_MIN - 300)
    }
  }, [epgData])

  // Synchronized scrolling
  const handleScroll = () => {
    if (!containerRef.current) return
    if (channelColRef.current) {
      channelColRef.current.scrollTop = containerRef.current.scrollTop
    }
    if (timeHeaderRef.current) {
      timeHeaderRef.current.scrollLeft = containerRef.current.scrollLeft
    }
  }

  const formatTime = (date: Date) => date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })

  const nowOffset = ((now.getTime() - timeStart.getTime()) / 60000) * PIXELS_PER_MIN

  // Program position
  const getProgramStyle = (program: EPGProgram) => {
    const pStart = new Date(program.start_time)
    const pEnd = new Date(program.end_time)
    const clampedStart = pStart < timeStart ? timeStart : pStart
    const clampedEnd = pEnd > timeEnd ? timeEnd : pEnd
    const leftMin = (clampedStart.getTime() - timeStart.getTime()) / 60000
    const durMin = (clampedEnd.getTime() - clampedStart.getTime()) / 60000
    return {
      left: leftMin * PIXELS_PER_MIN,
      width: Math.max(durMin * PIXELS_PER_MIN - 1, 40),
    }
  }

  // Channel logo color palette (deterministic per channel name)
  const getChannelColor = (name: string) => {
    const palette = [
      { bg: 'oklch(28% 0.10 25)', ink: 'oklch(85% 0.12 25)' },
      { bg: 'oklch(28% 0.10 250)', ink: 'oklch(85% 0.12 250)' },
      { bg: 'oklch(28% 0.10 140)', ink: 'oklch(85% 0.12 140)' },
      { bg: 'oklch(28% 0.10 50)', ink: 'oklch(85% 0.12 50)' },
      { bg: 'oklch(28% 0.10 320)', ink: 'oklch(85% 0.12 320)' },
      { bg: 'oklch(28% 0.10 200)', ink: 'oklch(85% 0.12 200)' },
    ]
    const idx = (name.charCodeAt(0) || 0) % palette.length
    return palette[idx]
  }

  return (
    <div
      className="flex flex-col h-full overflow-hidden gs-glass gs-grain relative"
      style={{ borderRadius: 14 }}
    >
      {/* Filter rail — glass pill buttons */}
      <div
        className="flex items-center gap-2 px-3 py-2 shrink-0"
        style={{
          background: 'var(--gs-glass-3)',
          borderBottom: '1px solid var(--gs-glass-stroke)',
        }}
      >
        <span
          className="gs-mono text-[10px] font-semibold shrink-0 mr-1"
          style={{ letterSpacing: '0.14em', color: 'var(--gs-ink-2)' }}
        >
          GUIDE
        </span>

        {/* Search */}
        <div className="relative flex-1 max-w-[180px]">
          <Search
            className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5"
            style={{ color: 'var(--gs-ink-3)' }}
          />
          <input
            type="text"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search..."
            className="w-full pl-7 pr-7 py-1.5 rounded-full text-xs focus:outline-none transition-all"
            style={{
              background: 'oklch(82% 0.02 250 / 0.06)',
              border: '1px solid var(--gs-glass-stroke)',
              color: 'var(--gs-ink-0)',
              fontFamily: 'inherit',
            }}
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')} className="absolute right-2 top-1/2 -translate-y-1/2 cursor-pointer">
              <X className="w-3.5 h-3.5" style={{ color: 'var(--gs-ink-3)' }} />
            </button>
          )}
        </div>

        {/* Category filter pills */}
        <div className="flex items-center gap-1 overflow-x-auto gs-scroll-hide">
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className="px-3 py-1 rounded-full text-[11px] font-medium whitespace-nowrap transition-all cursor-pointer"
              style={{
                background: selectedCategory === cat ? 'var(--gs-accent-soft)' : 'oklch(82% 0.02 250 / 0.06)',
                border: `1px solid ${selectedCategory === cat ? 'var(--gs-accent)' : 'var(--gs-glass-stroke)'}`,
                color: selectedCategory === cat ? 'var(--gs-accent)' : 'var(--gs-ink-1)',
                fontFamily: 'inherit',
                letterSpacing: '-0.005em',
              }}
            >
              {cat === 'all' ? 'All' : cat}
            </button>
          ))}
        </div>
      </div>

      {/* Guide grid area */}
      <div className="flex flex-1 overflow-hidden relative">

        {/* Fixed channel column */}
        <div className="shrink-0 z-20" style={{ width: CHANNEL_WIDTH, background: 'var(--gs-bg-0)' }}>
          {/* Corner cell */}
          <div
            className="flex items-center justify-between px-3"
            style={{
              height: HEADER_HEIGHT,
              background: 'var(--gs-glass-3)',
              borderBottom: '1px solid var(--gs-glass-stroke)',
              borderRight: '1px solid var(--gs-glass-stroke)',
            }}
          >
            <span
              className="gs-mono text-[10px] font-medium"
              style={{ letterSpacing: '0.14em', color: 'var(--gs-ink-2)' }}
            >
              CHANNELS
            </span>
          </div>

          {/* Channel names */}
          <div
            ref={channelColRef}
            className="overflow-hidden"
            style={{ height: `calc(100% - ${HEADER_HEIGHT}px)` }}
          >
            {filteredChannels.map(ch => {
              const playing = isChannelPlaying(ch.id)
              const colors = getChannelColor(ch.name)
              return (
                <div
                  key={ch.id}
                  onClick={() => onChannelSelect(ch)}
                  className="flex items-center gap-2.5 px-3 cursor-pointer transition-colors"
                  style={{
                    height: ROW_HEIGHT,
                    borderBottom: '1px solid var(--gs-glass-stroke)',
                    borderRight: '1px solid var(--gs-glass-stroke)',
                    background: playing ? 'var(--gs-accent-soft)' : 'transparent',
                  }}
                  onMouseEnter={e => {
                    if (!playing) e.currentTarget.style.background = 'oklch(82% 0.02 250 / 0.04)'
                  }}
                  onMouseLeave={e => {
                    if (!playing) e.currentTarget.style.background = 'transparent'
                  }}
                >
                  {ch.icon_url ? (
                    <img
                      src={ch.icon_url}
                      alt=""
                      className="w-9 h-9 rounded-lg object-contain shrink-0"
                      style={{
                        background: colors.bg,
                        border: '1px solid var(--gs-glass-stroke)',
                      }}
                    />
                  ) : (
                    <div
                      className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 gs-mono text-[10px] font-bold"
                      style={{
                        background: colors.bg,
                        color: colors.ink,
                        border: '1px solid var(--gs-glass-stroke)',
                      }}
                    >
                      {ch.name.substring(0, 3).toUpperCase()}
                    </div>
                  )}
                  <div className="min-w-0">
                    <div
                      className="gs-mono text-[9px] font-medium"
                      style={{ letterSpacing: '0.08em', color: 'var(--gs-ink-2)' }}
                    >
                      CH {ch.number || '—'}
                    </div>
                    <div
                      className="text-[12px] font-medium truncate leading-tight"
                      style={{ color: 'var(--gs-ink-0)' }}
                    >
                      {ch.name}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {/* Right side: time header + scrollable program grid */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Time header */}
          <div
            ref={timeHeaderRef}
            className="overflow-hidden shrink-0"
            style={{
              height: HEADER_HEIGHT,
              background: 'var(--gs-glass-3)',
              borderBottom: '1px solid var(--gs-glass-stroke)',
            }}
          >
            <div className="flex relative" style={{ width: gridWidth }}>
              {timeSlots.map((slot, i) => (
                <div
                  key={i}
                  className="shrink-0 flex items-center px-3 gs-mono text-[11px] font-medium"
                  style={{
                    width: 30 * PIXELS_PER_MIN,
                    height: HEADER_HEIGHT,
                    color: 'var(--gs-ink-2)',
                    letterSpacing: '0.06em',
                    borderRight: '1px solid var(--gs-glass-stroke)',
                  }}
                >
                  {formatTime(slot)}
                </div>
              ))}
            </div>
          </div>

          {/* Main scrollable grid */}
          <div
            ref={containerRef}
            className="flex-1 overflow-auto gs-scroll-hide"
            onScroll={handleScroll}
          >
            <div className="relative" style={{ width: gridWidth }}>
              {/* NOW line — accent colored with glow */}
              <div
                className="absolute top-0 bottom-0 z-10 pointer-events-none"
                style={{
                  left: nowOffset,
                  width: 2,
                  background: 'var(--gs-accent)',
                  boxShadow: '0 0 12px var(--gs-accent-glow)',
                }}
              >
                <div
                  className="absolute -left-[5px]"
                  style={{
                    top: -2,
                    width: 12,
                    height: 12,
                    borderRadius: 6,
                    background: 'var(--gs-accent)',
                    boxShadow: '0 0 12px var(--gs-accent-glow)',
                  }}
                />
                <span
                  className="gs-mono absolute text-[9px] font-bold"
                  style={{
                    top: 4,
                    left: 10,
                    letterSpacing: '0.14em',
                    color: 'var(--gs-accent)',
                    textShadow: '0 0 8px var(--gs-accent-glow)',
                  }}
                >
                  NOW
                </span>
              </div>

              {/* Program rows */}
              {filteredChannels.map(ch => {
                const programs = epgByChannel.get(ch.id) || []
                return (
                  <div
                    key={ch.id}
                    className="relative"
                    style={{
                      height: ROW_HEIGHT,
                      borderBottom: '1px solid var(--gs-glass-stroke)',
                    }}
                  >
                    {/* Grid lines every 30 min */}
                    {timeSlots.map((_, i) => (
                      <div
                        key={i}
                        className="absolute top-0 bottom-0"
                        style={{
                          left: i * 30 * PIXELS_PER_MIN,
                          borderRight: '1px solid oklch(82% 0.02 250 / 0.05)',
                        }}
                      />
                    ))}

                    {programs.length > 0 ? (
                      programs.map((prog, i) => {
                        const pos = getProgramStyle(prog)
                        const progStart = new Date(prog.start_time)
                        const progEnd = new Date(prog.end_time)
                        const isCurrent = now >= progStart && now < progEnd
                        return (
                          <div
                            key={prog.id || i}
                            className="absolute top-[3px] bottom-[3px] rounded-lg px-3 py-1 overflow-hidden cursor-pointer transition-all"
                            style={{
                              left: pos.left,
                              width: pos.width,
                              background: isCurrent ? 'var(--gs-accent-soft)' : 'oklch(82% 0.02 250 / 0.04)',
                              border: `1px solid ${isCurrent ? 'var(--gs-accent)' : 'var(--gs-glass-stroke)'}`,
                            }}
                            title={`${prog.title}\n${formatTime(progStart)} - ${formatTime(progEnd)}${prog.description ? '\n' + prog.description : ''}`}
                            onClick={() => {
                              const channel = channels.find(c => c.id === ch.id)
                              if (channel) onChannelSelect(channel)
                            }}
                            onMouseEnter={e => {
                              if (!isCurrent) e.currentTarget.style.background = 'oklch(82% 0.02 250 / 0.08)'
                            }}
                            onMouseLeave={e => {
                              if (!isCurrent) e.currentTarget.style.background = 'oklch(82% 0.02 250 / 0.04)'
                            }}
                          >
                            <div className="flex items-center gap-1.5 min-w-0">
                              {isCurrent && <span className="gs-live-dot" style={{ width: 6, height: 6 }} />}
                              <span
                                className="text-[12px] font-medium truncate leading-tight"
                                style={{ color: 'var(--gs-ink-0)' }}
                              >
                                {prog.title}
                              </span>
                            </div>
                            <div
                              className="gs-mono text-[9px] truncate leading-tight mt-0.5"
                              style={{ color: 'var(--gs-ink-3)', letterSpacing: '0.06em' }}
                            >
                              {formatTime(progStart)} - {formatTime(progEnd)}
                            </div>
                          </div>
                        )
                      })
                    ) : (
                      <div
                        className="absolute inset-0 flex items-center px-3 text-[11px] italic"
                        style={{ color: 'var(--gs-ink-3)' }}
                      >
                        No guide data
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
